
import React from 'react';

interface ServiceFeature {
  icon: string;
  title: string;
  description: string;
}

interface ServiceDetailProps {
  id: string;
  label: string;
  labelColor: string; // expected to be a valid CSS colour (e.g. "#ff0000")
  title: string;
  titleAccent: string;
  description: string;
  features: ServiceFeature[];
  image: string;
  imageAlt: string;
  reversed?: boolean;
}

/**
 * ServiceDetail – a presentational component that displays an image with
 * accompanying textual content and a list of features.
 *
 * The component is defensive: it validates required props at runtime and
 * provides graceful fall‑backs to avoid crashes in production.
 */
export default function ServiceDetail({
  id,
  label,
  labelColor,
  title,
  titleAccent,
  description,
  features = [], // defensive default
  image,
  imageAlt,
  reversed = false,
}: ServiceDetailProps) {
  // Basic runtime validation – helps catch mis‑configuration early.
  if (!id) {
    console.error('ServiceDetail: “id” prop is required.');
  }
  if (!label) {
    console.error('ServiceDetail: “label” prop is required.');
  }
  if (!labelColor) {
    console.error('ServiceDetail: “labelColor” prop is required.');
  }
  if (!title) {
    console.error('ServiceDetail: “title” prop is required.');
  }
  if (!titleAccent) {
    console.error('ServiceDetail: “titleAccent” prop is required.');
  }
  if (!description) {
    console.error('ServiceDetail: “description” prop is required.');
  }
  if (!image) {
    console.error('ServiceDetail: “image” prop is required.');
  }

  /**
   * Helper to build a semi‑transparent background colour from a hex value.
   * Tailwind’s arbitrary opacity syntax (e.g. “#ff0000/15”) isn’t understood by
   * the TypeScript parser inside a JSX attribute, so we construct the colour
   * manually.
   */
  const getLabelBackground = (hex: string) => {
    // Ensure the string starts with “#” and is 7 characters long (#RRGGBB)
    const normalized = hex.startsWith('#') ? hex : `#${hex}`;
    // Append 15 (hex for ~8% opacity) – result is #RRGGBB15
    return `${normalized}15`;
  };

  return (
    <section id={id} className="py-24 bg-white relative z-10">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        <div
          className={`
            grid lg:grid-cols-2 gap-16 items-center
            ${reversed ? 'lg:flex-row-reverse' : ''}
          `}
        >
          {/* Image */}
          <div className={reversed ? 'lg:order-2' : 'lg:order-1'}>
            <div className="relative w-full h-[480px] rounded-2xl overflow-hidden group">
              <img
                src={image}
                alt={imageAlt}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-matte-black/40 to-transparent"></div>
            </div>
          </div>

          {/* Content */}
          <div className={reversed ? 'lg:order-1' : 'lg:order-2'}>
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6"
              style={{
                backgroundColor: getLabelBackground(labelColor),
                color: labelColor,
              }}
            >
              {label}
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-matte-black mb-2 leading-tight">
              {title}
            </h2>
            <h3 className="text-4xl lg:text-5xl font-bold text-neon-green mb-6 leading-tight">
              {titleAccent}
            </h3>
            <p className="text-base text-gunmetal leading-relaxed mb-10">{description}</p>

            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 flex items-center justify-center bg-neon-green/10 rounded-lg flex-shrink-0 group-hover:bg-neon-green/20 transition-colors">
                    <i className={`${feature.icon} text-neon-green text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-matte-black mb-1">{feature.title}</h4>
                    <p className="text-xs text-gunmetal leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
